import {
  SITE_METADATA,
  buildCanonicalUrl,
  formatPageTitle,
  getDefaultOgImage,
} from '../config/siteMetadata';

export type PageMetaInput = {
  title?: string;
  description?: string;
  keywords?: string;
  ogType?: string;
  ogImage?: string;
  canonicalPath?: string;
  noIndex?: boolean;
};

function upsertMeta(attribute: 'name' | 'property', key: string, content: string) {
  if (!content) return;
  const selector = `meta[${attribute}="${key}"]`;
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  if (!href) return;
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

export function applyPageMeta(input: PageMetaInput = {}) {
  const pathname =
    input.canonicalPath ??
    (typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/');

  const title = formatPageTitle(input.title);
  const description = input.description?.trim() || SITE_METADATA.defaultDescription;
  const keywords = input.keywords?.trim() || SITE_METADATA.keywords;
  const ogType = input.ogType || SITE_METADATA.defaultOgType;
  const ogImage = input.ogImage || getDefaultOgImage();
  const canonicalUrl = buildCanonicalUrl(pathname.split('?')[0]);
  const robots = input.noIndex ? 'noindex, nofollow' : 'index, follow';

  document.title = title;
  document.documentElement.lang = 'fr';

  upsertMeta('name', 'description', description);
  upsertMeta('name', 'keywords', keywords);
  upsertMeta('name', 'author', SITE_METADATA.author);
  upsertMeta('name', 'robots', robots);
  upsertMeta('name', 'application-name', SITE_METADATA.siteName);
  upsertMeta('name', 'theme-color', SITE_METADATA.themeColor);

  upsertMeta('property', 'og:site_name', SITE_METADATA.siteName);
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:type', ogType);
  upsertMeta('property', 'og:locale', SITE_METADATA.locale);
  upsertMeta('property', 'og:url', canonicalUrl);
  upsertMeta('property', 'og:image', ogImage);

  upsertMeta('name', 'twitter:card', SITE_METADATA.twitterCard);
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', ogImage);

  upsertLink('canonical', canonicalUrl);
}

const ROUTE_META: Record<string, PageMetaInput> = {
  '/': {
    title: 'Accueil',
    description:
      'Choisissez votre formation, consultez le programme et inscrivez-vous en ligne — JCONSULT MY Academia, centre certifiant à Kinshasa.',
  },
  '/login': {
    title: 'Authentification',
    description: 'Connectez-vous ou créez votre compte apprenant JCONSULT MY Academia.',
    noIndex: true,
  },
  '/inscription': {
    title: 'Créer un compte',
    description: 'Inscription apprenant JCONSULT MY Academia — validation par e-mail (OTP).',
  },
  '/forgot-password': {
    title: 'Mot de passe oublié',
    noIndex: true,
  },
  '/verify-password-reset': {
    title: 'Code de réinitialisation',
    noIndex: true,
  },
  '/reset-password': {
    title: 'Réinitialisation mot de passe',
    noIndex: true,
  },
  '/legal/cgu': {
    title: "Conditions d'utilisation",
    description: "Conditions générales d'utilisation de la plateforme JCONSULT MY Academia.",
  },
  '/legal/confidentialite': {
    title: 'Politique de confidentialité',
    description: 'Politique de confidentialité et protection des données — JCONSULT MY Academia.',
  },
  '/excellence': {
    title: 'Tableau des excellences',
    description: 'Classement des apprenants et tableaux d’excellence JCONSULT MY Academia.',
  },
};

const PRIVATE_PREFIXES = ['/admin', '/app', '/formateur', '/partenaire'];

export function resolvePageMetaFromPath(pathname: string): PageMetaInput {
  const exact = ROUTE_META[pathname];
  if (exact) return exact;

  if (pathname.startsWith('/filieres/')) {
    return {
      title: 'Formations',
      description: 'Découvrez les filières professionnelles certifiantes proposées par JCONSULT MY Academia à Kinshasa.',
      canonicalPath: pathname,
    };
  }

  if (PRIVATE_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    if (pathname.startsWith('/admin/')) {
      return { title: 'Administration', noIndex: true };
    }
    if (pathname.startsWith('/app')) {
      return { title: 'Espace apprenant', noIndex: true };
    }
    if (pathname.startsWith('/formateur')) {
      return { title: 'Espace formateur', noIndex: true };
    }
    return { title: 'Espace partenaire', noIndex: true };
  }

  return {};
}
